import { useTranslator } from '@bluebeam/react-i18n';
import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCell,
  StdListHeaderCheckbox,
} from 'components/StdList';
import React, { useEffect, useState } from 'react';
import { RawStudioItem } from 'records/StudioItem';
import {
  StudioProjectFile,
  StudioProjectFileData,
} from 'records/StudioProjectFile';
import {
  StudioProjectFolder,
  StudioProjectFolderData,
} from 'records/StudioProjectFolder';
import { extractPDFs } from 'utils/general';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import StudioProjectsListRow from './StudioProjectsListRow';

interface Props extends WithAppStoreProps {
  searchInput?: string;
  pdfOnly?: boolean;
  folderSelectable?: boolean;
}

const StudioProjectsList: React.FC<Props> = ({
  searchInput,
  pdfOnly,
  folderSelectable,
  actions,
  appStore,
}: Props) => {
  const {
    studioProjects,
    studioCurrentFolder,
    studioCurrentFolderName,
    studioSelectedFiles,
    studioSelectedFolders,
    studioCurrentProjectId,
  } = appStore.state;

  const { translate } = useTranslator(['fileBrowser']);

  const gridTemplateColumns = '50px auto 150px';

  const [folders, setFolders] = useState<StudioProjectFolderData[]>([]);
  const [files, setFiles] = useState<StudioProjectFileData[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState('');

  const checkAllSelected = (): boolean => {
    const folderPdfDocuments = extractPDFs(studioCurrentFolder.files);

    const isChecked =
      folderPdfDocuments.size !== 0 &&
      folderPdfDocuments.size === studioSelectedFiles.length &&
      studioCurrentFolder.folders.size !== 0 &&
      studioCurrentFolder.folders.size === studioSelectedFolders.length;

    return isChecked;
  };

  const handleItemSelected = async (item: RawStudioItem): Promise<void> => {
    const currentSelectedFiles = studioSelectedFiles;
    const currentSelectedFolders = studioSelectedFolders;
    // TODO: place folder selection here
    const isFile = item.value.toString().includes('StudioProjectFile');
    if (isFile) {
      const selectedFile = new StudioProjectFile(item.value);
      const updatedFile = selectedFile.set(
        'studioprojectid',
        studioCurrentProjectId
      );

      const isChecked = studioSelectedFiles
        .flatMap((s) => s.id)
        .includes(updatedFile.id);
      if (!isChecked) {
        currentSelectedFiles.push(updatedFile);
      } else {
        const filtered = currentSelectedFiles.filter(
          (f) => f.id !== updatedFile.id
        );
        currentSelectedFiles.splice(
          0,
          currentSelectedFiles.length,
          ...filtered
        );
      }

      await actions.setStudioSelectedFiles(currentSelectedFiles);
    } else {
      const selectedFolder = new StudioProjectFolder(item.value);
      const updatedFolder = selectedFolder.set(
        'studioprojectid',
        studioCurrentProjectId
      );

      const isChecked = studioSelectedFolders
        .flatMap((s) => s.id)
        .includes(updatedFolder.id);
      if (!isChecked) {
        currentSelectedFolders.push(updatedFolder);
      } else {
        const filtered = currentSelectedFolders.filter(
          (f) => f.id !== updatedFolder.id
        );
        currentSelectedFolders.splice(
          0,
          currentSelectedFolders.length,
          ...filtered
        );
      }

      await actions.setStudioSelectedFolders(currentSelectedFolders);
    }
    const allChecked = checkAllSelected();
    setAllSelected(allChecked);
  };

  const handleOpenItem = async (item: RawStudioItem) => {
    let projectId = '';
    let folderId: string | number = '';

    switch (item.type) {
      case 'folder':
        projectId = currentProjectId;
        folderId = item.value.id;
        break;
      default:
        projectId = item.value.id.toString();
        await actions.setStudioProjectId(projectId);
        setCurrentProjectId(projectId);
        break;
    }
    await actions.setStudioFolderName(item.value.name);
    await actions.getStudioProjectFolder(projectId, folderId);
  };

  const createListRow = (item: RawStudioItem) => {
    const isFile = item.value.toString().includes('StudioProjectFile');
    const selectedEntity = isFile
      ? new StudioProjectFile(item.value).id
      : new StudioProjectFolder(item.value).id;

    const isChecked = (): boolean => {
      return (
        studioSelectedFiles.flatMap((s) => s.id).includes(selectedEntity) ||
        studioSelectedFolders.flatMap((s) => s.id).includes(selectedEntity)
      );
    };

    return (
      <StudioProjectsListRow
        onClick={
          isFile ? () => handleItemSelected(item) : () => handleOpenItem(item)
        }
        onChecked={
          item.type === 'folder' || item.type === 'file'
            ? () => handleItemSelected(item)
            : () => handleOpenItem(item)
        }
        checked={isChecked()}
        key={item.value.id}
        item={item}
        pdfOnly={pdfOnly}
        folderSelectable={folderSelectable}
      />
    );
  };

  const handleSelectAllChange = (): void => {
    const folderPdfDocuments = extractPDFs(studioCurrentFolder.files);
    const allChecked = checkAllSelected();
    const selectedFiles = !allChecked ? [...folderPdfDocuments] : [];
    actions.setStudioSelectedFiles(selectedFiles);
  };

  useEffect(() => {
    if (studioCurrentFolder) {
      const currentFolders = studioCurrentFolder.folders.toArray();
      const currentFiles = studioCurrentFolder.files.toArray();

      if (searchInput) {
        const filteredFolders = currentFolders.filter((f) =>
          f.name.includes(searchInput)
        );

        const filteredFiles = currentFiles.filter((f) =>
          f.name.includes(searchInput)
        );

        setFolders(filteredFolders);
        setFiles(filteredFiles);
      } else {
        setFolders(currentFolders);
        setFiles(currentFiles);
      }
    }

    const allChecked = checkAllSelected();
    setAllSelected(allChecked);
  }, [studioCurrentFolder, studioSelectedFiles, searchInput]);

  return (
    <StdList id="studio-project-list" gridTemplateColumns={gridTemplateColumns}>
      <StdListHeader>
        <StdListHeaderCheckbox
          id="studio-project-header-select-all-checkbox"
          indeterminate={false}
          checked={allSelected}
          onChange={() => handleSelectAllChange()}
        />
        <StdListHeaderCell text={translate('fileBrowser:name')} />
        <StdListHeaderCell text={translate('fileBrowser:lastModifiedDate')} />
      </StdListHeader>
      <StdListBody>
        {studioCurrentFolderName === '' &&
          studioProjects.map((p) =>
            createListRow({ value: p, type: 'project' })
          )}
        {studioCurrentFolder.folders &&
          folders.map((folder) =>
            createListRow({ value: folder, type: 'folder' })
          )}
        {studioCurrentFolder.files &&
          files.map((document) =>
            createListRow({ value: document, type: 'file' })
          )}
      </StdListBody>
    </StdList>
  );
};

StudioProjectsList.defaultProps = {
  searchInput: '',
  pdfOnly: false,
  folderSelectable: false,
};

export default withAppStore<Props>(StudioProjectsList);
